import * as React from 'react'
import Link from 'next/link'
import styled, { css } from 'styled-components'
import { useInView } from 'react-intersection-observer'

import * as ds from '@bob/design-system'
import { styles as S } from '@bob/design-system'
import { BrushTitleAnimated } from '@bob/components'
import { SITE_URL } from '@bob/config'
import * as url from '@bob/url'

import { Appear } from './Appear'
import { Brush } from './Brush'

export type HowProps = React.HTMLAttributes<HTMLDivElement>

function HowComponent({ ...rest }: HowProps): React.ReactElement {
  const inViewOptions = {
    threshold: 0.0,
    rootMargin: `0px 0px -40% 0px`,
    initialInView: false,
    triggerOnce: true,
  }

  const [$hero, inViewHero] = useInView(inViewOptions)
  const [$part2, inViewPart2] = useInView(inViewOptions)

  return (
    <HowSection {...rest}>
      <Hero ref={$hero}>
        <Wrapper>
          <Appear isVisible={inViewHero}>
            <S.heading.Title2>
              Une charte et un guide pour{' '}
              <Brush brushId="pink" inView={inViewHero} delay={0.5}>
                mieux consommer
              </Brush>
              &nbsp;: rejoignez nous !
            </S.heading.Title2>
          </Appear>
          <Appear isVisible={inViewHero} delay={0.3} hasTranslate={false}>
            <BlobSticker color="light-green">
              <ds.stickers.others.MoreLove />
            </BlobSticker>
          </Appear>
        </Wrapper>
        <S.copy.Text5>
          <Appear as="div" isVisible={inViewHero} delay={0.6}>
            <p>
              Sur Bien ou Bien, vous lirez des articles pour vous aider à mieux consommer. Dans notre guide,{' '}
              <Link href={url.brands({ bobChart: 'signatory' }).path}>
                <UnderlinedSpan>au-delà des marques signataires</UnderlinedSpan>
              </Link>{' '}
              vous retrouverez aussi d’autres marques repérées qui correspondent également à nos critères, même si elles
              ne sont pas encore signataires de la charte. Notre ambition, c’est de vous aider à mieux consommer au
              quotidien, quels que soient vos besoins.
            </p>
            <UnorderedList>
              <li>
                Vous êtes un consommateur ou une consommatrice engagée ?{' '}
                <Link href={url.newsletter().path}>
                  <UnderlinedSpan>Inscrivez-vous à notre newsletter</UnderlinedSpan>
                </Link>{' '}
                pour recevoir toute l’actualité du mouvement
              </li>
              <li>
                Vous êtes une marque ?{' '}
                <a href={'https://form.jotform.com/Fiona_ulule/rejoindre-guide-bienoubien'}>
                  <UnderlinedSpan>Engagez-vous ici</UnderlinedSpan>
                </a>{' '}
                en signant notre charte.
              </li>
              <li>
                Vous connaissez une marque qui aurait sa place dans notre guide ? Envoyez-nous un mail à :{' '}
                <UnderlinedSpan as="a" href="mailto:contact@bienoubien.com">
                  contact@bienoubien.com
                </UnderlinedSpan>
                , on se charge de la contacter !
              </li>
            </UnorderedList>
          </Appear>
        </S.copy.Text5>
      </Hero>
      <Body ref={$part2} inView={inViewPart2}>
        <BrushTitleAnimated
          text="À propos de Bien ou Bien"
          Component={S.heading.Title2}
          inView={inViewPart2}
          tag="h2"
        />
        <ImageText inView={inViewPart2}>
          <ImageTextPictureContainer>
            <Appear isVisible={inViewPart2} delay={1}>
              <ds.Picture
                src={SITE_URL + '/assets/rasterized/manifesto/manifest-04_1x.png'}
                srcSet={`${SITE_URL + '/assets/rasterized/manifesto/manifest-04_1x.png'} 1x, ${
                  SITE_URL + '/assets/rasterized/manifesto/manifest-04_2x.png'
                } 2x, ${SITE_URL + '/assets/rasterized/manifesto/manifest-04_3x.png'} 3x `}
                width="630"
                height="575"
              />
            </Appear>
            <Appear isVisible={inViewPart2} delay={1.2} hasTranslate={false}>
              <ds.stickers.others.UsefulKnowledge size={178} />
            </Appear>
          </ImageTextPictureContainer>
          <S.copy.Text5>
            <Appear isVisible={inViewPart2} delay={1.3}>
              <p>Comme vous, on a envie que le monde de demain soit vraiment bien.</p>
              <p>
                Commençons par nous présenter, ou nous re-présenter, car vous nous connaissez peut-être déjà. Bien ou
                Bien, c’est au départ l’union de deux entreprises engagées : Ulule et la Maif.
              </p>
              <p>
                Lors de notre premier tête-à-tête, on s’est aperçu qu’on avait vraiment beaucoup de points communs, et
                en particulier : l’envie farouche de contribuer à un futur plus ouvert, plus écologique, plus équilibré.
                Lancé en septembre 2021, Bien ou Bien est un guide des marques engagées. C’est un peu comme ce nouvel
                ami, en qui vous pouvez vous fier les yeux fermés pour vous aider à faire les bons choix pour consommer
                toujours plus durablement.
              </p>
              <p>
                De nombreux produits des marques repérées par Bien ou Bien sont disponibles à la vente sur Ulule
                Boutique, également propulsée par Ulule, en partenariat avec Maif. Bien ou Bien, c’est aussi une charte
                (que vous venez de lire juste au-dessus), avec des engagements clairs, et un événement pour mettre en
                lumière les marques, ces femmes et ces hommes qui portent de vrais projets de société. Le nom de cet
                événement ? Le Lundi Bien ou Bien. Pour être tenus au courant et suivre toute l’actualité du mouvement,
                n’hésitez pas à{' '}
                <Link href={url.newsletter().path}>
                  <UnderlinedSpan>vous inscrire à la newsletter.</UnderlinedSpan>
                </Link>
              </p>
            </Appear>
          </S.copy.Text5>
        </ImageText>
      </Body>
    </HowSection>
  )
}

const UnderlinedSpan = styled.span`
  text-decoration: underline;
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column-reverse;
  margin-bottom: 32px;

  @media screen and (${ds.breakpoint.LAPTOP}) {
    display: block;
    margin-bottom: 0;
  }

  ${S.heading.Title2} {
    overflow: hidden;
  }

  ${Brush} {
    &::before {
      transform: scaleY(0.6) scaleX(1.3);
    }
  }
`

const ImageTextPictureContainer = styled.div`
  position: relative;
  align-self: flex-start;
  grid-row: 1 / span 1;

  ${ds.Picture} {
    width: ${(395 / 335) * 100}%;
    margin-left: 15px;
    border-radius: 12px;
  }

  &:first-child {
    ${ds.Picture} {
      margin-right: 0;
      margin-left: -78px;
    }
  }

  @media screen and (${ds.breakpoint.TABLET}) {
    grid-row: auto;

    ${ds.Picture} {
      width: 100%;
      margin-right: -19%;
    }

    &:first-child {
      grid-column: 1 / span 5;

      ${ds.Picture} {
        margin-right: 0;
        margin-left: -19%;
        margin-top: 5%;
      }
    }

    &:last-child {
      grid-column: 8 / -1;
    }
  }
`

const ImageText = styled.div<{ inView?: boolean }>`
  display: grid;
  gap: 20px;
  margin-top: 50px;

  ${S.copy.Text5} {
    p {
      margin: 1.5em 0;

      &:first-child {
        margin-top: 0;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  ${S.copy.Text5},
  ${ImageTextPictureContainer} {
    opacity: 0;
    transform: translateY(30px);
    transition-delay: 100ms;
    transition-duration: 350ms;
    transition-property: opacity;
    transition-timing-function: ease-out;

    ${({ inView }) =>
      inView &&
      css`
        transform: translateY(0);
        opacity: 1;
      `}
  }

  @media screen and (${ds.breakpoint.TABLET}) {
    grid-template-columns: repeat(12, 1fr);

    ${S.copy.Text5} {
      display flex;

      &:first-child {
        grid-column: 1 / span 6;
      }

      &:last-child {
        grid-column: 6 / span 5;
      }
    }
  }

  @media screen and (${ds.breakpoint.LAPTOP}) {
    ${S.copy.Text5} {
      &:first-child {
        grid-column: 2 / span 5;
      }

      &:last-child {
        grid-column-end: span 6;
      }
    }
  }

  @media screen and (${ds.breakpoint.LAPTOP_L}) {
    gap: 36px;
  }
`

const Body = styled(S.container.Base)<{ inView: boolean }>`
  margin-top: 35px;

  ${ds.stickers.others.UsefulKnowledge} {
    background-color: ${ds.color.YELLOW};
    height: 105px;
    width: 105px;
  }

  ${ds.stickers.Sticker} {
    top: 0;
    right: 0;
    transform: translate(${(15 / 125) * 100}%, ${(-30 / 125) * 100}%);
  }

  ${S.copy.Text5} {
    padding-top: 15px;
  }

  ${BrushTitleAnimated} {
    text-align: center;
  }

  @media screen and (${ds.breakpoint.TABLET}) {
    margin-top: 114px;

    ${ds.stickers.others.UsefulKnowledge} {
      height: 178px;
      width: 178px;
    }

    ${BrushTitleAnimated} {
      display: block;
      text-align: right;
      margin: 0 0 0 auto;
    }

    ${ds.stickers.Sticker} {
      transform: translate(${(106 / 182) * 100}%, ${(-43 / 182) * 100}%);
    }

    ${S.copy.Text5} {
      padding-top: 0;
    }
  }
`

const UnorderedList = styled.ul`
  list-style: outside;
  margin-left: 20px;
  margin-top: 30px;
`

export const How = styled(HowComponent)`
  ${S.copy.Text5} {
    font-size: 18px; // HACK: Irregular font manipulation
  }

  @media screen and (${ds.breakpoint.TABLET}) {
    ${S.copy.Text5} {
      font-size: 24px; // HACK: Irregular font manipulation
    }
  }
`

const HowSection = styled.section`
  padding-top: 66px;
  padding-bottom: 92px;

  @media screen and (${ds.breakpoint.LAPTOP}) {
    padding-bottom: 96px;
  }
`

const BlobSticker = styled(ds.BlobBackground)`
  position: relative;
  display: block;
  width: 100%;
  max-width: 339px;
  margin: 0 auto;

  &:before {
    content: '';
    display: block;
    padding-bottom: 100%;
  }

  ${ds.stickers.Sticker} {
    position: absolute;
    width: ${(240 / 339) * 100}%;
    height: ${(240 / 339) * 100}%;
    top: ${(10 / 339) * 100}%;
    left: ${(41 / 339) * 100}%;
    background: none;
  }

  @media screen and (${ds.breakpoint.TABLET}) {
    margin-top: 30px;
  }

  @media screen and (${ds.breakpoint.LAPTOP}) {
    margin-top: 0;
  }
`

const ContainerGrid = styled(S.container.Base)`
  display: grid;
  grid-column-gap: 20px;

  @media screen and (${ds.breakpoint.LAPTOP}) {
    grid-template-columns: repeat(12, 1fr);
    grid-column-gap: 32px;
  }

  @media screen and (${ds.breakpoint.LAPTOP_L}) {
    grid-column-gap: 36px;
  }
`

const Hero = styled(ContainerGrid)`
  margin-bottom: 64px;

  ${S.heading.Title2} {
    text-align: center;
  }

  @media screen and (${ds.breakpoint.TABLET}) {
    grid-row-gap: 30px;
  }

  @media screen and (${ds.breakpoint.LAPTOP}) {
    margin-bottom: 90px;

    & > div:first-child {
      position: relative;
      grid-column: -9 / -2;
    }

    ${BlobSticker} {
      position: absolute;
      top: -50px;
      right: 100%;
      margin-right: 35px;
    }

    ${S.heading.Title2} {
      text-align: right;
    }

    ${S.copy.Text5} {
      grid-column: 4 / span 6;
    }
  }

  @media screen and (${ds.breakpoint.LAPTOP_L}) {
    grid-row-gap: 66px;
  }
`
